import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'
import { cabinetApi } from '../api'
import type { BBox } from '../api'
import { isobathZoomStep } from './isobaths'

/** Минимум, который хук просит у карты: один на все карты, как у районов. */
export interface BanMapSink {
  getMap: () => maplibregl.Map | null
  getBBox: () => BBox
  setBans: (data: GeoJSON.FeatureCollection) => void
  removeBans: () => void
}

const DEBOUNCE_MS = 250

export interface BanMeta {
  disclaimerKey: string | null
  /** Сколько запретов без геометрии (место названо без координат): на карте
   *  их нет, и легенда обязана это сказать. */
  withoutGeometry: number
  /** Нарисовал ли слой заливку; `null` — слой выключен или ответа ещё нет. */
  drawn: boolean | null
  /** Запреты на всю акваторию, действующие сейчас для вида (заголовки). */
  everywhere: string[]
}

const OFF: BanMeta = { disclaimerKey: null, withoutGeometry: 0, drawn: null, everywhere: [] }

interface EverywhereBan { title: string; active?: boolean; applies?: boolean }

/**
 * Запретные районы — тот же тумблер, что промысловые районы.
 *
 * В ОТЛИЧИЕ ОТ РАЙОНОВ У СЛОЯ ЕСТЬ ДАТА И ВИД: запрет «с 1 июля по 31 декабря»
 * или «специализированный промысел минтая» либо в силе для этого кадра, либо
 * нет. Поэтому запрос идёт заново на смене даты и вида, а не только кадра.
 */
export function useBansLayer(
  mapRef: RefObject<BanMapSink | null>,
  enabled: boolean,
  viewKey: string,
  date: string | null,
  species: number | null,
): BanMeta {
  const [meta, setMeta] = useState<BanMeta>(OFF)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeBans()
      setMeta(OFF)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      const zoom = isobathZoomStep(map.getMap()?.getZoom() ?? 6)
      cabinetApi
        .fishingBans({ bbox: map.getBBox(), zoom, date, species })
        .then(fc => {
          if (!alive) return
          if (!fc.features?.length) {
            map.removeBans()
          } else {
            map.setBans(fc as unknown as GeoJSON.FeatureCollection)
          }
          const everywhere = ((fc.meta as { everywhere?: EverywhereBan[] } | undefined)
            ?.everywhere ?? [])
            .filter(b => b.active && b.applies)
            .map(b => b.title)
          setMeta({
            disclaimerKey: fc.meta?.disclaimer_key ?? null,
            withoutGeometry: fc.meta?.without_geometry?.length ?? 0,
            drawn: Boolean(fc.features?.length),
            everywhere,
          })
        })
        .catch(() => {
          if (!alive) return
          map.removeBans()
          setMeta(OFF)
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewKey, date, species])

  useEffect(
    () => () => mapRef.current?.removeBans(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return meta
}
